import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatTimestamp, formatRelativeTime } from '../utils';
import { useFilters, useDebounce, useDataCache } from '../hooks';
import styles from './Devices.module.css';

/**
 * Devices Page
 * 
 * View sync activity grouped by device in a detailed list.
 * Uses global caching for fast navigation.
 */

interface AggregatedDevice {
  device_code: string;
  device_name: string | null;
  user_email: string | null;
  firmware_version: string | null;
  total_events: number;
  upload_count: number;
  download_count: number;
  corruption_count: number;
  error_count: number;
  last_sync: string;
  first_sync: string;
}

/**
 * Format relative time for last updated
 */
function formatLastUpdated(date: Date | null): string {
  if (!date) return 'Never';
  
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  
  if (diffSec < 5) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  
  const diffHours = Math.floor(diffMin / 60);
  return `${diffHours}h ago`;
}

export default function Devices() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<AggregatedDevice[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Filters - timeRange is shared across tabs
  const { timeRange, setTimeRange } = useFilters();
  const [searchInput, setSearchInput] = useState('');
  
  // Debounce search for smooth typing
  const debouncedSearch = useDebounce(searchInput, 300);
  
  // Global cache
  const { 
    fetchAllEvents, 
    getCachedAllEvents,
    hasAllEventsCache,
    isLoading: cacheLoading, 
    loadingMessage,
    error: cacheError,
    getLastFetchTime,
  } = useDataCache();

  const lastFetchTime = getLastFetchTime('allEvents');
  const hasCachedData = hasAllEventsCache(timeRange);

  // Aggregate events into devices
  const aggregateDevices = useCallback((events: { device_code?: string | null; device_name?: string | null; user_email?: string | null; firmware_version?: string | null; event_type?: string; has_suspicious_chars?: boolean; success?: boolean; timestamp: string }[]) => {
    setIsProcessing(true);
    
    const deviceMap = new Map<string, AggregatedDevice>();
    
    for (const event of events) {
      if (!event.device_code) continue;
      
      const existing = deviceMap.get(event.device_code);
      if (existing) {
        existing.total_events++;
        if (event.event_type === 'file_upload') existing.upload_count++;
        if (event.event_type === 'file_download') existing.download_count++;
        if (event.has_suspicious_chars) existing.corruption_count++;
        if (!event.success) existing.error_count++;
        if (new Date(event.timestamp) > new Date(existing.last_sync)) {
          existing.last_sync = event.timestamp;
          if (event.device_name) existing.device_name = event.device_name;
          if (event.firmware_version) existing.firmware_version = event.firmware_version;
        }
        if (new Date(event.timestamp) < new Date(existing.first_sync)) {
          existing.first_sync = event.timestamp;
        }
      } else {
        deviceMap.set(event.device_code, {
          device_code: event.device_code,
          device_name: event.device_name || null,
          user_email: event.user_email || null,
          firmware_version: event.firmware_version || null,
          total_events: 1,
          upload_count: event.event_type === 'file_upload' ? 1 : 0,
          download_count: event.event_type === 'file_download' ? 1 : 0,
          corruption_count: event.has_suspicious_chars ? 1 : 0,
          error_count: !event.success ? 1 : 0,
          last_sync: event.timestamp,
          first_sync: event.timestamp,
        });
      }
    }

    const deviceList = Array.from(deviceMap.values());
    deviceList.sort((a, b) => new Date(b.last_sync).getTime() - new Date(a.last_sync).getTime());
    
    setDevices(deviceList);
    setIsProcessing(false);
  }, []);

  // Load data (from cache or fetch)
  const loadData = useCallback(async (forceRefresh = false) => {
    const events = await fetchAllEvents(timeRange, forceRefresh);
    aggregateDevices(events);
  }, [timeRange, fetchAllEvents, aggregateDevices]);

  // Initial load - use cache if available
  useEffect(() => {
    const cached = getCachedAllEvents(timeRange);
    if (cached) {
      aggregateDevices(cached);
    } else {
      loadData();
    }
  }, [timeRange, getCachedAllEvents, aggregateDevices, loadData]);

  // Handle manual refresh
  const handleRefresh = useCallback(() => {
    loadData(true);
  }, [loadData]);

  // Filter devices by search (client-side, instant)
  const filteredDevices = useMemo(() => {
    if (!debouncedSearch.trim()) return devices;
    
    const searchLower = debouncedSearch.toLowerCase();
    return devices.filter(d => 
      d.device_code.toLowerCase().includes(searchLower) ||
      (d.device_name && d.device_name.toLowerCase().includes(searchLower)) ||
      (d.user_email && d.user_email.toLowerCase().includes(searchLower))
    );
  }, [devices, debouncedSearch]);

  const handleDeviceClick = (deviceCode: string) => {
    navigate(`/events?device_code=${encodeURIComponent(deviceCode)}`);
  };

  const handleClearSearch = () => {
    setSearchInput('');
  };

  const getHealthStatus = (device: AggregatedDevice) => {
    if (device.total_events === 0) return 'healthy';
    const corruptionRate = device.corruption_count / device.total_events;
    if (corruptionRate > 0.1) return 'critical';
    if (corruptionRate > 0.05) return 'warning';
    if (device.corruption_count > 0) return 'minor';
    return 'healthy';
  };

  const isLoading = cacheLoading || isProcessing;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Devices</h1>
          <p className={styles.subtitle}>
            {filteredDevices.length.toLocaleString()} devices with activity
            {hasCachedData && !cacheLoading && <span className={styles.cachedBadge}>Cached</span>}
          </p>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.lastUpdated}>
            Updated {formatLastUpdated(lastFetchTime)}
          </span>
          <button 
            className={styles.refreshButton} 
            onClick={handleRefresh}
            disabled={isLoading}
            title="Refresh data"
          >
            <span className={`${styles.refreshIcon} ${isLoading ? styles.spinning : ''}`}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchWrapper}>
          <input
            type="text"
            placeholder="Search by device code, name, or user..."
            className={styles.searchInput}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button 
              className={styles.clearSearchButton}
              onClick={handleClearSearch}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        <select 
          className={styles.select}
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
        >
          <option value="">All Time</option>
          <option value="1h">Last Hour</option>
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
        </select>
      </div>

      {cacheError && (
        <div className={styles.error}>
          {cacheError}
          <button className={styles.retryButton} onClick={handleRefresh}>
            Retry
          </button>
        </div>
      )}

      <div className={styles.tableContainer}>
        {isLoading && devices.length === 0 ? (
          <div className={styles.loading}>{loadingMessage || 'Loading devices...'}</div>
        ) : (
          <table className={`${styles.table} ${isLoading ? styles.tableLoading : ''}`}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Device Code</th>
                <th>Device Name</th>
                <th>User</th>
                <th>Firmware</th>
                <th>Events</th>
                <th>Uploads</th>
                <th>Downloads</th>
                <th>Corrupted</th>
                <th>Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {filteredDevices.length === 0 ? (
                <tr>
                  <td colSpan={10} className={styles.emptyRow}>
                    {searchInput ? 'No devices match your search.' : 'No devices found in the selected time range.'}
                  </td>
                </tr>
              ) : (
                filteredDevices.map((device) => {
                  const health = getHealthStatus(device);
                  return (
                    <tr 
                      key={device.device_code}
                      className={styles.clickableRow}
                      onClick={() => handleDeviceClick(device.device_code)}
                    >
                      <td className={styles.statusCell}>
                        <span className={`${styles.healthBadge} ${styles[`badge_${health}`]}`}>
                          {health === 'healthy' && '✓'}
                          {health === 'minor' && '!'}
                          {health === 'warning' && '⚠'}
                          {health === 'critical' && '🔴'}
                        </span>
                      </td>
                      <td>
                        <span className={styles.deviceCode}>{device.device_code}</span>
                      </td>
                      <td className={styles.nameCell}>
                        {device.device_name || <span className={styles.mutedText}>—</span>}
                      </td>
                      <td className={styles.userCell}>
                        {device.user_email || <span className={styles.mutedText}>—</span>}
                      </td>
                      <td>
                        <span className={styles.firmware}>{device.firmware_version || '—'}</span>
                      </td>
                      <td className={styles.numCell}>{device.total_events.toLocaleString()}</td>
                      <td className={styles.numCell}>{device.upload_count.toLocaleString()}</td>
                      <td className={styles.numCell}>{device.download_count.toLocaleString()}</td>
                      <td className={styles.numCell}>
                        {device.corruption_count > 0 ? (
                          <span className={styles.corruptedBadge}>⚠️ {device.corruption_count}</span>
                        ) : (
                          <span className={styles.okValue}>0</span>
                        )}
                      </td>
                      <td>
                        <div className={styles.timestamp}>
                          <span className={styles.timestampFull}>{formatTimestamp(device.last_sync)}</span>
                          <span className={styles.timestampRelative}>{formatRelativeTime(device.last_sync)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
